

































































































import Vue from 'vue';
import DvSkeleton from './DvSkeleton.vue';
import DvEmpty from './DvEmpty.vue';
import DvError from './DvError.vue';
import { IFilterChip } from '@/util/filterChips';
import { SortDir, SortKey, Density } from '@/util/browseQuery';

export type BrowseState = 'prompt' | 'loading' | 'results' | 'empty' | 'error' | 'end';

export interface ISortOption {
    key: SortKey;
    label: string;
}

interface IData {
    localValue: string;
    debounceTimer: number | null;
    observer: IntersectionObserver | null;
}

export default Vue.extend({
    name: 'DvBrowseFrame',
    components: { DvSkeleton, DvEmpty, DvError },
    props: {
        title: { type: String, default: '' },
        value: { type: String, default: '' },
        placeholder: { type: String, default: 'Search...' },
        minLength: { type: Number, default: 0 },
        count: { type: Number, default: 0 },
        state: { type: String as () => BrowseState, default: 'prompt' },
        chips: { type: Array as () => IFilterChip[], default: () => [] },
        sorts: { type: Array as () => ISortOption[], default: () => [] },
        sort: { type: String as () => SortKey, default: 'relevance' },
        dir: { type: String as () => SortDir, default: 'desc' },
        density: { type: String as () => Density, default: 'card' },
        showDensity: { type: Boolean, default: false },
        //  Surfaced to DvError so a failed search finally shows the server's
        //  actual reason instead of swallowing it silently.
        errorDetail: { type: String, default: '' },
    },
    data(): IData {
        return {
            localValue: this.value,
            debounceTimer: null,
            observer: null,
        };
    },
    computed: {
        showCount(): boolean {
            return this.state === 'results' || this.state === 'empty' || this.state === 'end';
        },
        countLabel(): string {
            return `${this.count.toLocaleString()} result${this.count === 1 ? '' : 's'}`;
        },
    },
    watch: {
        //  The parent can reset/replace the search text out-of-band (e.g. the
        //  "Clear all" chip-row action, or restoring state from the URL); keep
        //  the input in sync without fighting the user's own typing.
        value(now: string) {
            //  Skip the write-back when the only difference from what's in the
            //  box is surrounding whitespace: the parent stores a trimmed
            //  value (ItemSearchEx.onNameInput trims), so "iron " typed by the
            //  user would otherwise get overwritten with "iron" mid-keystroke,
            //  and the next character typed lands as "ironsword". Vue's own
            //  v-model.trim is exempt from write-back for the same reason.
            if (now !== this.localValue && now !== this.localValue.trim()) {
                this.localValue = now;
            }
        },
        localValue(now: string) {
            if (this.debounceTimer !== null) {
                window.clearTimeout(this.debounceTimer);
            }

            this.debounceTimer = window.setTimeout(() => {
                this.debounceTimer = null;
                //  Skip the emit when the debounced value already equals the
                //  parent's own prop: the value watcher above pulls a
                //  parent-driven change (e.g. restoring "ab" from the URL on a
                //  back/forward nav) into localValue, which re-triggers THIS
                //  watcher -- without this check that re-arms the timer and
                //  echoes 'input' back at the parent 300ms later for a value it
                //  already has, which can push a spurious history entry or
                //  duplicate search.
                //  No minLength gate here: below-minLength values (including
                //  "") must still be emitted so the parent's filter.name (and
                //  the URL) can clear when the user backspaces out of a
                //  search. ItemSearchEx.search() and SkillSearch.search()
                //  each check hasQuery first and return to 'prompt' without
                //  issuing a request, so this is safe to emit unconditionally.
                if (now !== this.value) {
                    this.$emit('input', now);
                }
            }, 300);
        },
        //  A page transitioning into 'results' can already have its sentinel on
        //  screen (a short first page, or a tall viewport) -- IntersectionObserver
        //  only fires on a threshold CROSSING, so a sentinel that was already
        //  intersecting while state was 'loading' never re-fires once state
        //  flips. Explicitly re-check on the transition, same as
        //  CashShopBrowser's maybeChainLoad.
        state(now: BrowseState) {
            if (now === 'results') {
                this.$nextTick(() => this.maybeLoadMore());
            }
        },
    },
    mounted() {
        this.observer = new IntersectionObserver((recs) => {
            if (recs.some((r) => r.isIntersecting)) {
                this.maybeLoadMore();
            }
        }, { rootMargin: '600px 0px' });
        this.observer.observe(this.$refs.sentinel as Element);
    },
    beforeDestroy() {
        if (this.debounceTimer !== null) {
            window.clearTimeout(this.debounceTimer);
        }

        if (this.observer) {
            this.observer.disconnect();
        }
    },
    methods: {
        //  Measure fresh rather than trusting the observer's cached flag: a
        //  cache-served page can append faster than the observer reports the
        //  sentinel leaving the viewport, which would otherwise chain-load
        //  the entire result set in one burst.
        maybeLoadMore() {
            if (this.state !== 'results') {
                return;
            }

            const el = this.$refs.sentinel as HTMLElement | undefined;
            if (el && el.getBoundingClientRect().top < window.innerHeight + 600) {
                this.$emit('loadMore');
            }
        },
        //  Rank chips carry the rank accent colour so that row reads at a
        //  glance (design spec). A 'ranks' chip never collapses a parent (the
        //  group has none), so its label is always the single rank's own
        //  name (e.g. "Ancient") -- lowercased, it matches one of the eight
        //  .rank-* classes below.
        chipRankClass(chip: IFilterChip): string {
            return chip.group === 'ranks' ? `rank-${chip.label.toLowerCase()}` : '';
        },
        onSortChange(evt: Event) {
            this.$emit('sort', (evt.target as HTMLSelectElement).value);
        },
        onDirToggle() {
            this.$emit('dir', this.dir === 'asc' ? 'desc' : 'asc');
        },
    },
});
